import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useIntl } from 'react-intl';
import useRestricted from '../hooks/useRestricted';
import { useNavigate } from 'react-router-dom';

export default function Checkout() {
    useRestricted();

    const intl = useIntl();
    const navigate = useNavigate();

    const tiers = [
        {
            url: "https://tilo.co/link/TWpReE1nPT18MQ==?encrypted=base64",
            title: intl.formatMessage({ id: "checkout.type1" }),
            price: '6',
            per: intl.formatMessage({ id: "checkout.perMonth" }),
            description: [
                intl.formatMessage({ id: "checkout.aiCoachMonthly" }),
                intl.formatMessage({ id: "checkout.terrain" }),
                intl.formatMessage({ id: "checkout.nutrition" }),
                intl.formatMessage({ id: "checkout.export" }),
                intl.formatMessage({ id: "checkout.analysis" }),
                intl.formatMessage({ id: "checkout.unlimited" }),
            ],
            buttonText: intl.formatMessage({ id: "checkout.buyNow" }),
            buttonVariant: 'contained',
        },
        {
            url: "https://tilo.co/link/TWpNMk53PT18MQ==?encrypted=base64",
            title: intl.formatMessage({ id: "checkout.type2" }),
            price: '3',
            per: intl.formatMessage({ id: "checkout.perYear" }),
            description: [
                intl.formatMessage({ id: "checkout.save24" }),
                intl.formatMessage({ id: "checkout.aiCoachAnnual" }),
                intl.formatMessage({ id: "checkout.terrain" }),
                intl.formatMessage({ id: "checkout.nutrition" }),
                intl.formatMessage({ id: "checkout.export" }),
                intl.formatMessage({ id: "checkout.analysis" }),
                intl.formatMessage({ id: "checkout.unlimited" }),
            ],
            buttonText: intl.formatMessage({ id: "checkout.buyNow" }),
            buttonVariant: 'contained',
        },
        {
            title: intl.formatMessage({ id: "checkout.type3" }),
            price: '97',
            per: intl.formatMessage({ id: "checkout.oneTime" }),
            badge: intl.formatMessage({ id: "checkout.bestValue" }),
            description: [
                intl.formatMessage({ id: "checkout.lifetimeAccess" }),
                intl.formatMessage({ id: "checkout.payOnce" }),
                intl.formatMessage({ id: "checkout.aiCoachLifetime" }),
                intl.formatMessage({ id: "checkout.terrain" }),
                intl.formatMessage({ id: "checkout.nutrition" }),
                intl.formatMessage({ id: "checkout.export" }),
                intl.formatMessage({ id: "checkout.analysis" }),
                intl.formatMessage({ id: "checkout.unlimited" }),
            ],
            buttonText: intl.formatMessage({ id: "checkout.getLifetime" }),
            buttonVariant: 'contained',
        },
    ];

    const goToPayment = () => {
        navigate({
            pathname: "/payment-lifetime"
        })
    }

    return (
        <Container
            id="pricing"
            sx={{
                pt: { xs: 4, sm: 12 },
                pb: { xs: 8, sm: 16 },
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: { xs: 3, sm: 6 },
            }}
        >
            <Box
                sx={{
                    width: { sm: '100%', md: '60%' },
                    textAlign: { sm: 'left', md: 'center' },
                }}
            >
                <Typography component="h2" variant="h4" color="text.primary">
                    {intl.formatMessage({ id: "checkout.title" })}
                </Typography>
            </Box>
            <Grid container spacing={3} alignItems="stretch" justifyContent="center">
                {tiers.map((t) => (
                    <Grid key={t.title} item xs={12} sm={6} md={4}>
                        <Card
                            sx={{
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                                height: '100%',
                                ...(t.badge && {
                                    border: '2px solid',
                                    borderColor: 'primary.main',
                                }),
                            }}
                        >
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box
                                    sx={{
                                        mb: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography component="h3" variant="h6" fontWeight="bold">
                                        {t.title}
                                    </Typography>
                                    {t.badge && (
                                        <Chip label={t.badge} color="primary" size="small" />
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                    <Typography component="h3" variant="h2">
                                        ${t.price}
                                    </Typography>
                                    <Typography component="h3" variant="h6">
                                        <Box display="inline-flex" alignItems="center" gap={0.5}>&nbsp; {t.per}</Box>
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 2, opacity: 0.2, borderColor: 'grey.500' }} />
                                {t.description.map((line) => (
                                    <Box
                                        key={line}
                                        sx={{ py: 1, display: 'flex', gap: 1.5, alignItems: 'center' }}
                                    >
                                        <CheckCircleRoundedIcon sx={{ width: 20, color: 'primary.main' }} />
                                        <Typography component="text" variant="subtitle2">
                                            {line}
                                        </Typography>
                                    </Box>
                                ))}
                            </CardContent>
                            <CardActions>
                                <Button
                                    fullWidth
                                    variant={t.buttonVariant as 'outlined' | 'contained'}
                                    href={t.url}
                                    onClick={!t.url ? () => goToPayment() : undefined}
                                >
                                    {t.buttonText}
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Box
                sx={{
                    width: { sm: '100%', md: '60%' },
                    textAlign: { sm: 'left', md: 'center' },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <Typography fontWeight="bold" fontStyle="italic" fontSize={14} variant="body1" color="text.primary">
                    {intl.formatMessage({ id: "checkout.note" })}
                </Typography>
                <Typography fontStyle="italic" fontSize={12} variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: "checkout.apiNote" })}
                </Typography>
            </Box>
        </Container>
    );
}